import { supabase } from '@/integrations/supabase/client';
import { 
  UnifiedMetric, 
  UnifiedMaterial, 
  UserMetricData, 
  UserMaterialData,
  MetricFilters,
  MaterialFilters 
} from '@/types/unifiedLibrary';

class UnifiedLibraryService {
  private static instance: UnifiedLibraryService;
  private cache: Map<string, any> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): UnifiedLibraryService {
    if (!UnifiedLibraryService.instance) {
      UnifiedLibraryService.instance = new UnifiedLibraryService();
    }
    return UnifiedLibraryService.instance;
  }

  // Metrics Library Operations
  async getMetrics(filters?: MetricFilters): Promise<UnifiedMetric[]> {
    const cacheKey = `metrics_${JSON.stringify(filters)}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      let query = supabase
        .from('unified_metrics_library')
        .select('*')
        .eq('is_active', true);

      if (filters) {
        if (filters.category) {
          query = query.eq('category', filters.category);
        }
        if (filters.subcategory) {
          query = query.eq('subcategory', filters.subcategory);
        }
        if (filters.difficulty) {
          query = query.eq('difficulty', filters.difficulty);
        }
        if (filters.scope) {
          query = query.eq('scope', filters.scope);
        }
        if (filters.mandatory !== undefined) {
          query = query.eq('mandatory', filters.mandatory);
        }
        if (filters.search) {
          query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }
        if (filters.industry) {
          query = query.contains('industry', [filters.industry]);
        }
      }

      const { data, error } = await query.order('name');
      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching metrics:', error);
      throw error;
    }
  }

  async getMetricById(id: string): Promise<UnifiedMetric | null> {
    const cacheKey = `metric_${id}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('unified_metrics_library')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching metric by ID:', error);
      throw error;
    }
  }

  async getMetricCategories(): Promise<string[]> {
    const cacheKey = 'metric_categories';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('unified_metrics_library')
        .select('category')
        .eq('is_active', true);

      if (error) throw error;

      const categories = data ? [...new Set(data.map(item => item.category))] : [];
      this.setCached(cacheKey, categories);
      return categories;
    } catch (error) {
      console.error('Error fetching metric categories:', error);
      throw error;
    }
  }

  async getMetricSubcategories(category: string): Promise<string[]> {
    const cacheKey = `metric_subcategories_${category}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('unified_metrics_library')
        .select('subcategory')
        .eq('category', category)
        .eq('is_active', true);

      if (error) throw error;

      const subcategories = data ? [...new Set(data.map(item => item.subcategory))] : [];
      this.setCached(cacheKey, subcategories);
      return subcategories;
    } catch (error) {
      console.error('Error fetching metric subcategories:', error);
      throw error;
    }
  }

  // Get metrics by standard
  async getMetricsByStandard(standard: string): Promise<UnifiedMetric[]> {
    const cacheKey = `metrics_by_standard_${standard}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('unified_metrics_library')
        .select('*')
        .eq('is_active', true)
        .contains('standards', { [standard]: [] });

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error(`Error fetching metrics by standard ${standard}:`, error);
      throw error;
    }
  }

  // Get comprehensive standards coverage analysis
  async getStandardsCoverage(selectedStandards: string[]): Promise<any> {
    const cacheKey = `standards_coverage_${selectedStandards.sort().join('_')}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data: metrics, error } = await supabase
        .from('unified_metrics_library')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const coverage = this.analyzeStandardsCoverage(selectedStandards, metrics || []);
      this.setCached(cacheKey, coverage);
      return coverage;
    } catch (error) {
      console.error('Error fetching standards coverage:', error);
      throw error;
    }
  }

  // Analyze standards coverage for selected standards
  private analyzeStandardsCoverage(standards: string[], metrics: UnifiedMetric[]): any {
    const coverage = {
      totalMetrics: metrics.length,
      totalCategories: new Set(metrics.map(m => m.category)).size,
      standards: {} as any,
      categoryCoverage: {} as any,
      ifrsAlignment: {} as any,
      sasbIndustryMapping: {} as any
    };

    // Initialize standards coverage
    standards.forEach(standard => {
      coverage.standards[standard] = {
        metrics: 0,
        categories: new Set(),
        ifrsS1Count: 0,
        ifrsS2Count: 0,
        sasbCount: 0
      };
    });

    // Initialize category coverage
    const categories = [...new Set(metrics.map(m => m.category))];
    categories.forEach(category => {
      coverage.categoryCoverage[category] = [];
    });

    // Analyze each metric
    metrics.forEach(metric => {
      let metricHasStandards = false;
      
      standards.forEach(standard => {
        if (metric.standards && metric.standards[standard]) {
          coverage.standards[standard].metrics++;
          coverage.standards[standard].categories.add(metric.category);
          coverage.categoryCoverage[metric.category].push(standard);
          metricHasStandards = true;

          // Count IFRS and SASB alignments
          if (metric.standards.IFRS_S1) {
            coverage.standards[standard].ifrsS1Count++;
          }
          if (metric.standards.IFRS_S2) {
            coverage.standards[standard].ifrsS2Count++;
          }
          if (metric.standards.SASB) {
            coverage.standards[standard].sasbCount++;
          }
        }
      });
      
      // If metric has no standards, add to general category
      if (!metricHasStandards) {
        if (!coverage.categoryCoverage[metric.category]) {
          coverage.categoryCoverage[metric.category] = [];
        }
        coverage.categoryCoverage[metric.category].push('General');
      }
    });

    // Convert Sets to counts for standards
    standards.forEach(standard => {
      coverage.standards[standard].categories = coverage.standards[standard].categories.size;
    });

    // Remove empty categories and sort
    Object.keys(coverage.categoryCoverage).forEach(category => {
      if (coverage.categoryCoverage[category].length === 0) {
        delete coverage.categoryCoverage[category];
      } else {
        // Remove duplicates and sort
        coverage.categoryCoverage[category] = [...new Set(coverage.categoryCoverage[category])].sort();
      }
    });

    // Analyze IFRS alignment
    coverage.ifrsAlignment = this.analyzeIFRSAlignment(metrics);

    // Analyze SASB industry mapping
    coverage.sasbIndustryMapping = this.analyzeSASBIndustryMapping(metrics);

    return coverage;
  }

  // Analyze IFRS S1/S2 alignment
  private analyzeIFRSAlignment(metrics: UnifiedMetric[]): any {
    const ifrsAnalysis = {
      ifrsS1: {
        total: 0,
        byCategory: {} as any,
        requirements: ['General', 'Strategy', 'Risk Management', 'Metrics', 'Targets']
      },
      ifrsS2: {
        total: 0,
        byCategory: {} as any,
        requirements: ['Climate', 'Governance', 'Strategy', 'Risk Management', 'Metrics', 'Targets']
      }
    };

    metrics.forEach(metric => {
      if (metric.standards.IFRS_S1) {
        ifrsAnalysis.ifrsS1.total++;
        if (!ifrsAnalysis.ifrsS1.byCategory[metric.category]) {
          ifrsAnalysis.ifrsS1.byCategory[metric.category] = 0;
        }
        ifrsAnalysis.ifrsS1.byCategory[metric.category]++;
      }

      if (metric.standards.IFRS_S2) {
        ifrsAnalysis.ifrsS2.total++;
        if (!ifrsAnalysis.ifrsS2.byCategory[metric.category]) {
          ifrsAnalysis.ifrsS2.byCategory[metric.category] = 0;
        }
        ifrsAnalysis.ifrsS2.byCategory[metric.category]++;
      }
    });

    return ifrsAnalysis;
  }

  // Analyze SASB industry mapping
  private analyzeSASBIndustryMapping(metrics: UnifiedMetric[]): any {
    const sasbAnalysis = {
      total: 0,
      byIndustry: {} as any,
      byCategory: {} as any,
      industryCodes: new Set<string>()
    };

    metrics.forEach(metric => {
      if (metric.standards.SASB && Array.isArray(metric.standards.SASB)) {
        sasbAnalysis.total++;
        
        // Count by category
        if (!sasbAnalysis.byCategory[metric.category]) {
          sasbAnalysis.byCategory[metric.category] = 0;
        }
        sasbAnalysis.byCategory[metric.category]++;

        // Collect industry codes
        metric.standards.SASB.forEach((code: string) => {
          sasbAnalysis.industryCodes.add(code);
          
          // Extract industry from code (e.g., EN-IF-410a.1 -> EN)
          const industry = code.split('-')[0];
          if (!sasbAnalysis.byIndustry[industry]) {
            sasbAnalysis.byIndustry[industry] = 0;
          }
          sasbAnalysis.byIndustry[industry]++;
        });
      }
    });

    // Convert Set to array
    sasbAnalysis.industryCodes = Array.from(sasbAnalysis.industryCodes);

    return sasbAnalysis;
  }

  // Get IFRS standards mapping
  async getIFRSStandardsMapping(): Promise<any[]> {
    const cacheKey = 'ifrs_standards_mapping';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('ifrs_standards_mapping')
        .select('*')
        .eq('is_active', true)
        .order('standard_code');

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching IFRS standards mapping:', error);
      throw error;
    }
  }

  // Get metrics with comprehensive standards analysis
  async getMetricsWithStandardsAnalysis(filters?: MetricFilters): Promise<any> {
    const metrics = await this.getMetrics(filters);
    const ifrsMapping = await this.getIFRSStandardsMapping();
    
    return {
      metrics,
      ifrsMapping,
      standardsAnalysis: this.analyzeStandardsForMetrics(metrics, ifrsMapping)
    };
  }

  // Analyze standards for a set of metrics
  private analyzeStandardsForMetrics(metrics: UnifiedMetric[], ifrsMapping: any[]): any {
    const analysis = {
      totalMetrics: metrics.length,
      standardsCoverage: {} as any,
      ifrsCompliance: {} as any,
      sasbIndustryCoverage: {} as any
    };

    // Analyze standards coverage
    const allStandards = new Set<string>();
    metrics.forEach(metric => {
      if (metric.standards) {
        Object.keys(metric.standards).forEach(standard => {
          allStandards.add(standard);
        });
      }
    });

    Array.from(allStandards).forEach(standard => {
      const standardMetrics = metrics.filter(m => 
        m.standards && m.standards[standard]
      );
      
      analysis.standardsCoverage[standard] = {
        count: standardMetrics.length,
        percentage: Math.round((standardMetrics.length / metrics.length) * 100),
        categories: [...new Set(standardMetrics.map(m => m.category))]
      };
    });

    // Analyze IFRS compliance
    analysis.ifrsCompliance = {
      s1: {
        total: metrics.filter(m => m.standards?.IFRS_S1).length,
        byCategory: {} as any
      },
      s2: {
        total: metrics.filter(m => m.standards?.IFRS_S2).length,
        byCategory: {} as any
      }
    };

    // Count IFRS S1/S2 by category
    metrics.forEach(metric => {
      if (metric.standards?.IFRS_S1) {
        if (!analysis.ifrsCompliance.s1.byCategory[metric.category]) {
          analysis.ifrsCompliance.s1.byCategory[metric.category] = 0;
        }
        analysis.ifrsCompliance.s1.byCategory[metric.category]++;
      }

      if (metric.standards?.IFRS_S2) {
        if (!analysis.ifrsCompliance.s2.byCategory[metric.category]) {
          analysis.ifrsCompliance.s2.byCategory[metric.category] = 0;
        }
        analysis.ifrsCompliance.s2.byCategory[metric.category]++;
      }
    });

    // Analyze SASB industry coverage
    const sasbMetrics = metrics.filter(m => m.standards?.SASB);
    analysis.sasbIndustryCoverage = {
      total: sasbMetrics.length,
      byIndustry: {} as any,
      industryCodes: new Set<string>()
    };

    sasbMetrics.forEach(metric => {
      if (Array.isArray(metric.standards.SASB)) {
        metric.standards.SASB.forEach((code: string) => {
          analysis.sasbIndustryCoverage.industryCodes.add(code);
          
          const industry = code.split('-')[0];
          if (!analysis.sasbIndustryCoverage.byIndustry[industry]) {
            analysis.sasbIndustryCoverage.byIndustry[industry] = 0;
          }
          analysis.sasbIndustryCoverage.byIndustry[industry]++;
        });
      }
    });

    // Convert Set to array
    analysis.sasbIndustryCoverage.industryCodes = Array.from(analysis.sasbIndustryCoverage.industryCodes);

    return analysis;
  }

  // User Metrics Data Operations
  async getUserMetrics(): Promise<UserMetricData[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_metrics_data')
        .select(`
          *,
          metric:unified_metrics_library(*)
        `)
        .eq('organization_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user metrics:', error);
      throw error;
    }
  }

  async addUserMetric(metricId: string, customValue: number, customMetadata?: Record<string, any>): Promise<UserMetricData> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the metric details from the library
      const metric = await this.getMetricById(metricId);
      if (!metric) throw new Error('Metric not found');

      const { data, error } = await supabase
        .from('user_metrics_data')
        .insert({
          organization_id: user.id,
          metric_id: metricId,
          name: metric.name,
          category: metric.category,
          subcategory: metric.subcategory,
          description: metric.description,
          unit: metric.unit,
          currency: metric.currency,
          custom_value: customValue,
          custom_metadata: customMetadata || {},
          is_custom: false
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding user metric:', error);
      throw error;
    }
  }

  async updateUserMetric(id: string, updates: Partial<UserMetricData>): Promise<UserMetricData> {
    try {
      const { data, error } = await supabase
        .from('user_metrics_data')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user metric:', error);
      throw error;
    }
  }

  async updateUserMaterial(id: string, updates: Partial<UserMaterialData>): Promise<UserMaterialData> {
    try {
      const { data, error } = await supabase
        .from('user_materials_data')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user material:', error);
      throw error;
    }
  }

  async deleteUserMetric(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_metrics_data')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting user metric:', error);
      throw error;
    }
  }

  async deleteUserMaterial(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_materials_data')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting user material:', error);
      throw error;
    }
  }

  // Materials Library Operations
  async getMaterials(filters?: MaterialFilters): Promise<UnifiedMaterial[]> {
    const cacheKey = `materials_${JSON.stringify(filters)}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      let query = supabase
        .from('unified_materials_library')
        .select('*')
        .eq('is_active', true);

      if (filters) {
        if (filters.category) {
          query = query.eq('category', filters.category);
        }
        if (filters.subcategory) {
          query = query.eq('subcategory', filters.subcategory);
        }
        if (filters.scope) {
          query = query.eq('scope', filters.scope);
        }
        if (filters.search) {
          query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }
        if (filters.sustainability_rating) {
          query = query.eq('sustainability_rating', filters.sustainability_rating);
        }
      }

      const { data, error } = await query.order('name');
      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching materials:', error);
      throw error;
    }
  }

  async getMaterialCategories(): Promise<string[]> {
    const cacheKey = 'material_categories';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('unified_materials_library')
        .select('category')
        .eq('is_active', true);

      if (error) throw error;

      const categories = data ? [...new Set(data.map(item => item.category))] : [];
      this.setCached(cacheKey, categories);
      return categories;
    } catch (error) {
      console.error('Error fetching material categories:', error);
      throw error;
    }
  }

  async getUserMaterials(): Promise<UserMaterialData[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_materials_data')
        .select(`
          *,
          material:unified_materials_library(*)
        `)
        .eq('organization_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user materials:', error);
      throw error;
    }
  }

  // Create user metric from library
  async createMetricFromLibrary(metricId: string, customData: {
    custom_value?: number;
    custom_metadata?: Record<string, any>;
  }): Promise<UserMetricData> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the metric details from the library
      const metric = await this.getMetricById(metricId);
      if (!metric) {
        console.error('Metric not found with ID:', metricId);
        throw new Error('Metric not found');
      }
      
      console.log('Found metric:', metric);

      console.log('Creating metric from library:', {
        organization_id: user.id,
        metric_id: metricId,
        name: metric.name,
        category: metric.category,
        subcategory: metric.subcategory,
        description: metric.description,
        unit: metric.unit,
        currency: metric.currency,
        custom_value: customData.custom_value || 0,
        custom_metadata: customData.custom_metadata || {},
        is_custom: false
      });

      const { data, error } = await supabase
        .from('user_metrics_data')
        .insert({
          organization_id: user.id,
          metric_id: metricId,
          name: metric.name,
          category: metric.category,
          subcategory: metric.subcategory,
          description: metric.description,
          unit: metric.unit,
          currency: metric.currency,
          custom_value: customData.custom_value || 0,
          custom_metadata: customData.custom_metadata || {},
          is_custom: false
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Successfully created metric:', data);
      return data;
    } catch (error) {
      console.error('Error creating metric from library:', error);
      throw error;
    }
  }

  // Create user material from library
  async createMaterialFromLibrary(materialId: string, customData: {
    custom_density?: number;
    custom_carbon_footprint?: number;
    custom_cost_per_unit?: number;
    custom_metadata?: Record<string, any>;
  }): Promise<UserMaterialData> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the material details from the library
      const { data: material, error: materialError } = await supabase
        .from('unified_materials_library')
        .select('*')
        .eq('id', materialId)
        .single();

      if (materialError || !material) throw new Error('Material not found');

      console.log('Creating material from library:', {
        organization_id: user.id,
        material_id: materialId,
        name: material.name,
        category: material.category,
        subcategory: material.subcategory,
        description: material.description,
        unit: material.unit,
        currency: material.currency,
        custom_density: customData.custom_density,
        custom_carbon_footprint: customData.custom_carbon_footprint,
        custom_cost_per_unit: customData.custom_cost_per_unit,
        custom_metadata: customData.custom_metadata || {},
        is_custom: false
      });

      const { data, error } = await supabase
        .from('user_materials_data')
        .insert({
          organization_id: user.id,
          material_id: materialId,
          name: material.name,
          category: material.category,
          subcategory: material.subcategory,
          description: material.description,
          unit: material.unit,
          density: material.density,
          carbon_footprint: material.carbon_footprint,
          cost_per_unit: material.cost_per_unit,
          currency: material.currency,
          custom_density: customData.custom_density,
          custom_carbon_footprint: customData.custom_carbon_footprint,
          custom_cost_per_unit: customData.custom_cost_per_unit,
          custom_metadata: customData.custom_metadata || {},
          is_custom: false
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Successfully created material:', data);
      return data;
    } catch (error) {
      console.error('Error creating material from library:', error);
      throw error;
    }
  }

  // Debug method to check library data
  async debugLibraryData(): Promise<{ metrics: number; materials: number }> {
    try {
      const { data: metrics, error: metricsError } = await supabase
        .from('unified_metrics_library')
        .select('id, name, category')
        .eq('is_active', true)
        .limit(5);

      const { data: materials, error: materialsError } = await supabase
        .from('unified_materials_library')
        .select('id, name, category')
        .eq('is_active', true)
        .limit(5);

      console.log('Library debug data:', {
        metrics: metrics || [],
        materials: materials || [],
        metricsError,
        materialsError
      });

      return {
        metrics: metrics?.length || 0,
        materials: materials?.length || 0
      };
    } catch (error) {
      console.error('Error debugging library data:', error);
      return { metrics: 0, materials: 0 };
    }
  }

  // Cache management
  private getCached(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCached(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const unifiedLibraryService = UnifiedLibraryService.getInstance(); 